import { apiClient, isHttpClientError } from "@/http";
import type { EpisodeStatus } from "@/modules/dataset/public";
import { apiRoutes } from "./apiRoutes";
import type { PageQuery, PageResp } from "./page";

/**
 * SAM 어노테이션 프록시 경계(backend `docs/specs/sam-annotation-proxy.md`).
 *
 * Studio의 GPU/디코더 요청은 training-infra로 중계한다. Studio 세트는 포인트 프롬프트를
 * 저장한다. preview의 마스크 PNG/contour는 저장하지 않는다. 파일 번들은 별도 declare/finalize로
 * PNG/YAML을 등록하고, 읽기 전용 세트로 조회한다.
 * 캔버스 프레임은 반드시 `frame.png` 에서 가져온다. preview 는 프레임을 돌려주지 않는다
 * (upstream `frame_jpeg_base64` 를 의도적으로 버림). 같은 holder 디코드를 써야 좌표가
 * SAM 과 일치한다.
 */

// ---- 읽기 평면 ----

// backend `service/training/annotation/read.py` 의 structure page 와 1:1.
export type AnnotationEpisodeStructure = {
  name: string;
  status: EpisodeStatus;
};

export type AnnotationStructureQuery = {
  camera?: string;
  page: number;
  size: number;
  includeExcluded: boolean;
};

export type AnnotationStructureResp = {
  cameras: string[];
  camera: string | null;
  items: AnnotationEpisodeStructure[];
  total: number;
  page: number;
  size: number;
};

// 서명 mp4 URL(TTL 1h). <video> 가 직접 읽는다. apiClient 로 프록시하지 않는다(rules §7).
export type AnnotationVideoUrlResp = {
  url: string;
};

export type AnnotationFrameMetaResp = {
  num_frames: number;
  fps: number;
};

// ---- 프롬프트/미리보기 (backend `service/training/annotation/predict.py`) ----

// 포인트는 이미지 natural-pixel 좌표(원점 좌상단)다. label 은 1=foreground/0=background.
// foreground 가 1개도 없으면 서버가 422 로 거절한다. obj_id 는 camera 안에서만 유일한 식별자다.
export type AnnotationPromptObject = {
  obj_id: number;
  points: number[][];
  labels: (0 | 1)[];
};

export type AnnotationPreviewReq = {
  camera: string;
  episode: string;
  frame_index: number;
  // 활성 프레임의 occupied slot 전부를 한 요청에 담는다(서버 상한 32).
  objects: AnnotationPromptObject[];
};

// polygon 꼭짓점은 {x,y} 객체다. 원본(training-infra FE)의 숫자쌍 배열과 다르다.
export type AnnotationMaskContour = {
  object_id: number;
  polygons: { x: number; y: number }[][];
};

// backend `service/training/upstream/schema.py::TrainingMaskPreviewRef` 와 1:1.
export type AnnotationPreviewResp = {
  width: number;
  height: number;
  // 모든 객체의 union 마스크 PNG. 객체별 구분은 contours 로 한다.
  mask_png_b64: string;
  contours: AnnotationMaskContour[];
};

// ---- 세트 (backend `service/training/annotation/sets.py`) ----

export type AnnotationSetStatus = "draft" | "committed";

export type AnnotationMaterializeStatus = "queued" | "running" | "succeeded" | "failed";

export type AnnotationFrameTargetWrite = {
  obj_id: number;
  object_name?: string | null;
  points: number[][];
  labels: (0 | 1)[];
};

export type AnnotationSetFrameWriteReq = {
  expected_revision: number;
  camera: string;
  episode: string;
  frame_index: number;
  targets: AnnotationFrameTargetWrite[];
};

export type AnnotationSetCreateReq = {
  name?: string | null;
  initial_frame?: AnnotationSetFrameWriteReq | null;
  source_set_uuid?: string | null;
  expected_source_revision?: number | null;
};

// BE `entity/training/annotation/set.py` 의 컬럼 길이와 같은 값. 넘는 값을 보내면 422 가 온다.
export const ANNOTATION_SET_NAME_MAX_LENGTH = 255;

export type AnnotationSetRenameReq = {
  name: string | null;
};

// materialize 잡 상태(세트당 단일 slot). 진행률 = rendered_count / requested_count 다.
// 단위는 distinct (camera, episode, frame_index) keyframe 수다.
export type AnnotationSetMaterializeView = {
  status: AnnotationMaterializeStatus;
  requested_count: number;
  rendered_count: number;
  error: Record<string, unknown> | null;
};

export type AnnotationSetOrigin = "studio" | "upload_bundle" | "import_bundle";
export type AnnotationBundleSummary = {
  keyframe_count: number;
  cameras: readonly { camera: string; keyframe_count: number; object_count: number }[];
};

export type AnnotationSetSummaryView = {
  uuid: string;
  dataset_uuid: string;
  name: string | null;
  status: AnnotationSetStatus;
  // origin 필드가 없는 응답은 Studio 세트다.
  // @compat ZERG-626 until=be:affbb7f2
  origin?: AnnotationSetOrigin;
  // 구 응답에는 번들 요약 필드가 없다.
  // @compat ZERG-626 until=be:affbb7f2
  bundle?: AnnotationBundleSummary | null;
  revision: number;
  annotated_frame_count: number;
  annotated_target_count: number;
  fingerprint: string | null;
  materialized_path: string | null;
  materialize: AnnotationSetMaterializeView | null;
  bundle_index: AnnotationBundleIndexView | null;
  created_at: string;
  modified_at: string;
};

export type AnnotationSetFrameSummaryView = {
  frame_id: string;
  camera: string;
  episode: string;
  frame_index: number;
  timestamp_seconds: number;
  revision: string;
  annotated_target_ids: string[];
};

export type AnnotationSetFrameTargetView = AnnotationFrameTargetWrite & {
  target_id: string;
  object_name: string | null;
};

export type AnnotationSetFrameView = AnnotationSetFrameSummaryView & {
  targets: AnnotationSetFrameTargetView[];
};

export type AnnotationSetFrameIndexView = {
  set_uuid: string;
  set_revision: number;
  revision: number;
  ordered_frames: AnnotationSetFrameSummaryView[];
};

export type AnnotationSetFrameMutationReceipt = {
  set_uuid: string;
  set_revision: number;
  saved_frame_index_revision: number;
  frame: AnnotationSetFrameSummaryView | null;
  deleted_frame_id: string | null;
};

export type AnnotationSetCreateView = AnnotationSetSummaryView & {
  initial_frame: AnnotationSetFrameSummaryView | null;
  saved_frame_index_revision: number;
};

export type AnnotationTrackingConditioningView = {
  set_uuid: string;
  set_revision: number;
  camera: string;
  keyframes: AnnotationSetFrameView[];
};

export type AnnotationRequestOptions = {
  // preview/frame 은 최신 요청만 유효하다(latest-wins). 이전 요청은 호출부가 abort 한다.
  signal?: AbortSignal;
};

// ---- 읽기 평면 fetcher ----

/** 카메라 목록과 선택 카메라의 에피소드 한 페이지를 조회한다. */
export async function getAnnotationStructure(
  datasetUuid: string,
  query: AnnotationStructureQuery,
  options: { signal?: AbortSignal } = {},
): Promise<AnnotationStructureResp> {
  const response = await apiClient.get<AnnotationStructureResp>(
    apiRoutes.trainingAnnotation.structure(datasetUuid),
    {
      params: {
        camera: query.camera,
        page: query.page,
        size: query.size,
        include_excluded: query.includeExcluded,
      },
      signal: options.signal,
    },
  );
  return response.data;
}

export async function getAnnotationVideoUrl(
  datasetUuid: string,
  camera: string,
  episode: string,
  options: { signal?: AbortSignal } = {},
): Promise<AnnotationVideoUrlResp> {
  const response = await apiClient.get<AnnotationVideoUrlResp>(
    apiRoutes.trainingAnnotation.videoUrl(datasetUuid),
    { params: { camera, episode }, signal: options.signal },
  );
  return response.data;
}

/** 프레임 수/fps. 조회지만 training 디코더를 실행한다. */
export async function getAnnotationFrameMeta(
  datasetUuid: string,
  camera: string,
  episode: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationFrameMetaResp> {
  const response = await apiClient.get<AnnotationFrameMetaResp>(
    apiRoutes.trainingAnnotation.frameMeta(datasetUuid),
    { params: { camera, episode }, signal: options.signal },
  );
  return response.data;
}

/**
 * 단건 프레임 PNG. 인증 헤더가 필요해 <img src> 로 직접 읽을 수 없다. 그래서 blob 으로 받는다.
 * objectURL 변환·수명 관리는 호출부(frame store)가 소유한다.
 */
export async function fetchAnnotationFrameBlob(
  datasetUuid: string,
  camera: string,
  episode: string,
  frameIndex: number,
  options: AnnotationRequestOptions = {},
): Promise<Blob> {
  const response = await apiClient.get<Blob>(apiRoutes.trainingAnnotation.framePng(datasetUuid), {
    params: { camera, episode, frame_index: frameIndex },
    responseType: "blob",
    signal: options.signal,
  });
  return response.data;
}

/** 포인트 프롬프트 → 마스크 미리보기. 429+Retry-After 는 정상 경로다(GPU holder 직렬화). */
export async function requestAnnotationPreview(
  datasetUuid: string,
  payload: AnnotationPreviewReq,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationPreviewResp> {
  const response = await apiClient.post<AnnotationPreviewResp>(
    apiRoutes.trainingAnnotation.preview(datasetUuid),
    payload,
    { signal: options.signal },
  );
  return response.data;
}

// ---- 세트 fetcher ----

export async function listAnnotationSets(datasetUuid: string): Promise<AnnotationSetSummaryView[]> {
  const response = await apiClient.get<AnnotationSetSummaryView[]>(
    apiRoutes.trainingAnnotation.sets(datasetUuid),
  );
  return response.data;
}

export async function getAnnotationSet(
  setUuid: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetSummaryView> {
  const response = await apiClient.get<AnnotationSetSummaryView>(
    apiRoutes.trainingAnnotation.set(setUuid),
    { signal: options.signal },
  );
  return response.data;
}

export async function createAnnotationSet(
  datasetUuid: string,
  payload: AnnotationSetCreateReq,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetCreateView> {
  const response = await apiClient.post<AnnotationSetCreateView>(
    apiRoutes.trainingAnnotation.sets(datasetUuid),
    payload,
    { signal: options.signal },
  );
  return response.data;
}

export async function renameAnnotationSet(
  setUuid: string,
  payload: AnnotationSetRenameReq,
): Promise<AnnotationSetSummaryView> {
  const response = await apiClient.patch<AnnotationSetSummaryView>(
    apiRoutes.trainingAnnotation.set(setUuid),
    payload,
  );
  return response.data;
}

export async function deleteAnnotationSet(
  setUuid: string,
  expectedRevision: number,
): Promise<void> {
  await apiClient.delete(apiRoutes.trainingAnnotation.set(setUuid), {
    params: { expected_revision: expectedRevision },
  });
}

/** fingerprint 를 고정하고 materialize 잡을 enqueue 한다. 진행 상태는 세트를 다시 조회해 materialize 로 본다. */
export async function commitAnnotationSet(
  setUuid: string,
  expectedRevision: number,
): Promise<AnnotationSetSummaryView> {
  const response = await apiClient.post<AnnotationSetSummaryView>(
    apiRoutes.trainingAnnotation.setCommit(setUuid),
    { expected_revision: expectedRevision },
  );
  return response.data;
}

/** failed 잡을 명시적으로 다시 시도한다(202). queued/running/succeeded 는 409 다. */
export async function materializeAnnotationSet(
  setUuid: string,
  expectedRevision: number,
): Promise<AnnotationSetSummaryView> {
  const response = await apiClient.post<AnnotationSetSummaryView>(
    apiRoutes.trainingAnnotation.setMaterialize(setUuid),
    { expected_revision: expectedRevision },
  );
  return response.data;
}

// 세트 폴더 기준 파일 1건과 그 파일의 signed GET URL(backend `service/training/annotation/set_files.py`).
export type AnnotationSetFileView = {
  // `<camera>/<file>`. 예) `wrist/wrist_0.png`.
  relative_path: string;
  size_bytes: number | null;
  url: string;
  expires_at: string;
};

/**
 * 렌더가 끝난 세트의 keyframe PNG/YAML 한 페이지. draft·렌더 전 세트는 409 다
 * (`training.annotation_set_not_committed` · `training.annotation_set_not_materialized`).
 */
export async function listAnnotationSetFiles(
  setUuid: string,
  query: PageQuery,
  options: AnnotationRequestOptions = {},
): Promise<PageResp<AnnotationSetFileView>> {
  const response = await apiClient.get<PageResp<AnnotationSetFileView>>(
    apiRoutes.trainingAnnotation.setFiles(setUuid),
    { params: { page: query.page, size: query.size }, signal: options.signal },
  );
  return response.data;
}

export async function listAnnotationSetFrames(
  setUuid: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetFrameIndexView> {
  const response = await apiClient.get<AnnotationSetFrameIndexView>(
    apiRoutes.trainingAnnotation.setFrames(setUuid),
    { signal: options.signal },
  );
  return response.data;
}

export async function getAnnotationSetFrame(
  setUuid: string,
  frameId: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetFrameView> {
  const response = await apiClient.get<AnnotationSetFrameView>(
    apiRoutes.trainingAnnotation.setFrame(setUuid, frameId),
    { signal: options.signal },
  );
  return response.data;
}

export async function saveAnnotationSetFrame(
  setUuid: string,
  frameId: string,
  payload: AnnotationSetFrameWriteReq,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetFrameMutationReceipt> {
  const response = await apiClient.put<AnnotationSetFrameMutationReceipt>(
    apiRoutes.trainingAnnotation.setFrame(setUuid, frameId),
    payload,
    { signal: options.signal },
  );
  return response.data;
}

export async function deleteAnnotationSetFrame(
  setUuid: string,
  frameId: string,
  expectedRevision: number,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationSetFrameMutationReceipt> {
  const response = await apiClient.delete<AnnotationSetFrameMutationReceipt>(
    apiRoutes.trainingAnnotation.setFrame(setUuid, frameId),
    {
      params: { expected_revision: expectedRevision },
      signal: options.signal,
    },
  );
  return response.data;
}

export async function getAnnotationTrackingConditioning(
  setUuid: string,
  camera: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationTrackingConditioningView> {
  const response = await apiClient.get<AnnotationTrackingConditioningView>(
    apiRoutes.trainingAnnotation.trackingConditioning(setUuid),
    { params: { camera }, signal: options.signal },
  );
  return response.data;
}

// ---- 트래킹 connection (backend `service/training/annotation/connection.py`) ----

export type AnnotationConnectionKeyframeReq = {
  camera: string;
  episode: string;
  frame_index: number;
};

export type AnnotationConnectionReq = {
  // 추적 대상 (camera, episode).
  camera: string;
  episode: string;
  // 컨디셔닝 keyframe 좌표(≤128). 대상과 다른 episode 도 허용한다. camera 는 같아야 한다.
  keyframes?: AnnotationConnectionKeyframeReq[];
};

// 요청 keyframes[i] 와 응답 init.keyframes[i] 는 index 로 대응한다. FE 는 좌표
// (camera/episode/frame_index)를 같은 순서로 보관해야 한다. 그래야 echo 판정 등에 쓸 수 있다.
export type AnnotationConnectionKeyframeInit = {
  video: string;
  frame_index: number;
};

export type AnnotationConnectionInit = {
  // 추적 대상 mp4 의 서명 URL. WS onopen init 메시지의 video 필드로 보낸다.
  video: string;
  keyframes: AnnotationConnectionKeyframeInit[];
};

export type AnnotationConnectionResp = {
  // training 직결 WS 주소. 하드코딩하지 않고 이 값을 그대로 쓴다.
  ws_url: string;
  init: AnnotationConnectionInit;
  // 포함된 서명 URL 중 가장 이른 만료 시각(TTL 6h). 만료가 가까우면 다시 발급한다.
  expires_at: string;
};

/**
 * 트래킹 직결 WS 의 connection 을 발급한다. WS 자격은 토큰이 아니라 서명 video URL 을 가진
 * 것이다. 여기서 registry 게이트를 통과하면 이후 WS 는 인증 없이 직접 연결된다.
 */
export async function createAnnotationConnection(
  datasetUuid: string,
  payload: AnnotationConnectionReq,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationConnectionResp> {
  const response = await apiClient.post<AnnotationConnectionResp>(
    apiRoutes.trainingAnnotation.connection(datasetUuid),
    payload,
    { signal: options.signal },
  );
  return response.data;
}

// ---- 에러 판별 ----

// preview 혼잡(429). 실패가 아니라 순번 대기다. retryAfterSeconds(기본 2초) 후 다시 시도한다.
export function isAnnotationUpstreamBusy(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.annotation_upstream_busy";
}

// committed 세트를 수정하려고 했다(409). 자동 복구는 없다. 새 draft 를 만들도록 안내한다.
export function isAnnotationSetCommitted(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.annotation_set_committed";
}

export function isAnnotationSetRevisionConflict(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.annotation_set_revision_conflict";
}

// lazy 등록이 아직 준비되지 않았다(409). 잠시 후 다시 들어오도록 안내한다.
export function isAnnotationDatasetNotReady(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "dataset.not_ready";
}

/** 파일 번들에는 Episode/frame 좌표가 없어 Studio 동작을 연결하지 않는다. */
export function isAnnotationBundleSet(set: AnnotationSetSummaryView | null): boolean {
  return set?.origin === "upload_bundle" || set?.origin === "import_bundle";
}

export type AnnotationBundleUploadTicket = {
  relative_path: string;
  upload_url: string;
  method: "PUT";
  headers: Readonly<Record<string, string>>;
  expires_at: string;
};
export type AnnotationBundleImportView = {
  set_uuid: string;
  files: readonly AnnotationBundleUploadTicket[];
};
export type AnnotationBundleFinalizeView = {
  set: AnnotationSetSummaryView;
  warnings: readonly string[];
};

export async function declareAnnotationBundleImport(
  datasetUuid: string,
  files: readonly { relative_path: string; size_bytes: number }[],
  options: AnnotationRequestOptions = {},
): Promise<AnnotationBundleImportView> {
  return (
    await apiClient.post<AnnotationBundleImportView>(
      apiRoutes.trainingAnnotation.bundleImports(datasetUuid),
      { files },
      { signal: options.signal },
    )
  ).data;
}

export async function finalizeAnnotationBundleImport(
  datasetUuid: string,
  setUuid: string,
  payload: { name: string | null; files: readonly string[] },
  options: AnnotationRequestOptions = {},
): Promise<AnnotationBundleFinalizeView> {
  return (
    await apiClient.post<AnnotationBundleFinalizeView>(
      apiRoutes.trainingAnnotation.bundleFinalize(datasetUuid, setUuid),
      payload,
      { signal: options.signal },
    )
  ).data;
}

/** 명시적으로 공개된 중복 UUID만 읽는다. 로그 context나 다른 409 값은 사용하지 않는다. */
export function duplicateAnnotationBundleUuid(error: unknown): string | null {
  if (!isHttpClientError(error) || error.code !== "training.annotation_bundle_duplicate")
    return null;
  const body = error.details;
  if (typeof body !== "object" || body === null || !("params" in body)) return null;
  const params = body.params;
  if (typeof params !== "object" || params === null || !("annotation_set_uuid" in params))
    return null;
  const uuid = params.annotation_set_uuid;
  return typeof uuid === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(uuid)
    ? uuid
    : null;
}

export type AnnotationBundleIndexView = {
  status: "unindexed" | "queued" | "running" | "ready" | "failed";
  requested_count: number;
  processed_count: number;
  error: { stage: string; code: string; message: string; relative_path: string | null } | null;
};

export type AnnotationBundleFileView = {
  relative_path: string;
  generation: string;
  size_bytes: number;
  url: string;
  expires_at: string;
};

export type AnnotationBundleFrameSummary = {
  uuid: string;
  camera: string;
  width: number;
  height: number;
  object_count: number;
  point_count: number;
  png: AnnotationBundleFileView;
};

export type AnnotationBundleObject = {
  ordinal: number;
  source_index: string | null;
  points: readonly (readonly [number, number])[];
  labels: readonly (0 | 1)[];
};

export type AnnotationBundleFrameDetail = AnnotationBundleFrameSummary & {
  yaml: AnnotationBundleFileView;
  objects: readonly AnnotationBundleObject[];
};

export async function requestAnnotationBundleIndex(
  setUuid: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationBundleIndexView> {
  return (
    await apiClient.post<AnnotationBundleIndexView>(
      apiRoutes.trainingAnnotation.bundleIndex(setUuid),
      {},
      { signal: options.signal },
    )
  ).data;
}

export async function listAnnotationBundleFrames(
  setUuid: string,
  query: PageQuery & { camera?: string },
  options: AnnotationRequestOptions = {},
): Promise<PageResp<AnnotationBundleFrameSummary>> {
  return (
    await apiClient.get<PageResp<AnnotationBundleFrameSummary>>(
      apiRoutes.trainingAnnotation.bundleFrames(setUuid),
      { params: query, signal: options.signal },
    )
  ).data;
}

export async function getAnnotationBundleFrame(
  setUuid: string,
  frameUuid: string,
  options: AnnotationRequestOptions = {},
): Promise<AnnotationBundleFrameDetail> {
  return (
    await apiClient.get<AnnotationBundleFrameDetail>(
      apiRoutes.trainingAnnotation.bundleFrame(setUuid, frameUuid),
      { signal: options.signal },
    )
  ).data;
}
